import React, { useMemo } from 'react';
import {
  ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceArea,
  ScatterChart, Scatter, ZAxis,
} from 'recharts';
import { Activity, BatteryCharging, Thermometer, ListOrdered, Scale, Sigma } from 'lucide-react';
import ChartCard from './ChartCard';
import ChartZoomHeader from './ChartZoomHeader';
import { useChartZoom } from './useChartZoom';
import {
  SERIES_COLORS, CHART_GRID, CHART_AXIS, AXIS_TICK_STYLE, AXIS_LABEL_STYLE,
  TOOLTIP_CONTENT_STYLE, TOOLTIP_LABEL_STYLE, LEGEND_STYLE,
} from './chartTheme';
import { LIMITER_ORDER, LIMITER_SHORT_LABELS } from '../../../lib/regenBrakingTools';

const REGEN_COLOR = SERIES_COLORS[2];
const FRICTION_COLOR = SERIES_COLORS[7];
const SPEED_COLOR = SERIES_COLORS[3];

// Limiter colours: "full regen" neutral, stability red, battery limits in one violet family.
export const LIMITER_COLORS = {
  none: SERIES_COLORS[2],
  strategy: SERIES_COLORS[0],
  decel_cap: SERIES_COLORS[3],
  speed_fade: '#94A3B8',
  stability: SERIES_COLORS[7],
  motor: SERIES_COLORS[1],
  battery_c_rate: SERIES_COLORS[6],
  battery_temp: SERIES_COLORS[4],
  battery_soc: SERIES_COLORS[6],
  battery_voltage: SERIES_COLORS[6],
  max_soc: SERIES_COLORS[5],
};

const timeTick = (v) => Number(v).toFixed(1);

// Event charts (force split, regen power, limiter) on a multi-hour range run would pack dozens
// of cycle loops into one width and read as a solid block, so they open zoomed to the first
// cycle pass; Reset Zoom shows the whole run.
function useEventZoom(data, windowS) {
  const last = data.length ? data[data.length - 1].time_s : 0;
  const initial = windowS && last > windowS * 1.5 ? [0, windowS] : null;
  return useChartZoom(['dataMin', 'dataMax'], initial);
}

const firstPassCaption = (base, zoom, windowS) => (zoom.isZoomed && windowS ? `${base} (first ${windowS} s)` : base);
const CHART_HEIGHT = 300;

function useSeriesData(series) {
  return useMemo(() => {
    if (!series) return [];
    return series.time_s.map((t, i) => {
      const braking = !!series.limiter[i];
      return {
        time_s: t,
        speed_kph: series.speed_kph[i],
        regen_kn: series.regen_force_kn[i],
        friction_kn: series.friction_force_kn[i],
        regen_power_kw: braking ? series.regen_power_kw[i] : null,
        charge_limit_kw: braking ? series.charge_limit_kw[i] : null,
        soc_pct: series.soc_pct[i],
        temp_c: series.battery_temp_c[i],
        limiter: series.limiter[i],
        cum_regen_kwh: series.cumulative_regen_kwh[i],
        cum_friction_kwh: series.cumulative_friction_kwh[i],
      };
    });
  }, [series]);
}

function TimeAxis({ zoom }) {
  return (
    <XAxis
      dataKey="time_s" type="number" domain={zoom.xDomain} allowDataOverflow
      tick={AXIS_TICK_STYLE} axisLine={{ stroke: CHART_GRID }} tickLine={false} tickFormatter={timeTick}
      label={{ value: 'Time (s)', position: 'insideBottom', offset: -4, style: AXIS_LABEL_STYLE }}
    />
  );
}

const leftLabel = (value) => ({ value, angle: -90, position: 'insideLeft', style: { ...AXIS_LABEL_STYLE, textAnchor: 'middle' } });
const rightLabel = (value) => ({ value, angle: 90, position: 'insideRight', style: { ...AXIS_LABEL_STYLE, textAnchor: 'middle' } });

function ZoomArea({ zoom, yAxisId }) {
  return zoom.isSelecting ? (
    <ReferenceArea yAxisId={yAxisId} x1={zoom.refAreaLeft} x2={zoom.refAreaRight} stroke={CHART_AXIS} fill={CHART_AXIS} fillOpacity={0.15} />
  ) : null;
}

const zoomHandlers = (zoom) => ({ onMouseDown: zoom.onMouseDown, onMouseMove: zoom.onMouseMove, onMouseUp: zoom.onMouseUp });
const fmt = (d) => (v) => (v == null ? '—' : Number(v).toFixed(d));

export function BrakingForceSplitChart({ series, windowS }) {
  const data = useSeriesData(series);
  const zoom = useEventZoom(data, windowS);
  if (!data.length) return null;
  return (
    <ChartCard icon={Activity} title="Braking Force Split" testId="chart-regen-force-split"
      description="Braking force at the wheels shared between regen (motor) and friction brakes, with vehicle speed.">
      <ChartZoomHeader caption={firstPassCaption('Force & Speed vs Time', zoom, windowS)} isZoomed={zoom.isZoomed} onReset={zoom.resetZoom} testId="zoom-reset-force-split" />
      <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
        <ComposedChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 4 }} {...zoomHandlers(zoom)}>
          <CartesianGrid stroke={CHART_GRID} vertical={false} />
          <TimeAxis zoom={zoom} />
          <YAxis yAxisId="f" tick={AXIS_TICK_STYLE} axisLine={{ stroke: CHART_GRID }} tickLine={false} width={52} label={leftLabel('Force (kN)')} />
          <YAxis yAxisId="v" orientation="right" tick={AXIS_TICK_STYLE} axisLine={{ stroke: CHART_GRID }} tickLine={false} width={52} label={rightLabel('Speed (km/h)')} />
          <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} labelFormatter={(v) => `t: ${timeTick(v)} s`} formatter={fmt(2)} />
          <Legend wrapperStyle={LEGEND_STYLE} />
          <Area yAxisId="f" type="stepAfter" dataKey="regen_kn" name="Regen (kN)" stackId="brake" stroke={REGEN_COLOR} fill={REGEN_COLOR} fillOpacity={0.55} isAnimationActive={false} />
          <Area yAxisId="f" type="stepAfter" dataKey="friction_kn" name="Friction (kN)" stackId="brake" stroke={FRICTION_COLOR} fill={FRICTION_COLOR} fillOpacity={0.55} isAnimationActive={false} />
          <Line yAxisId="v" type="monotone" dataKey="speed_kph" name="Speed (km/h)" stroke={SPEED_COLOR} dot={false} strokeWidth={1.25} isAnimationActive={false} />
          <ZoomArea zoom={zoom} yAxisId="f" />
        </ComposedChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

export function RegenPowerChart({ series, windowS }) {
  const data = useSeriesData(series);
  const zoom = useEventZoom(data, windowS);
  if (!data.length) return null;
  return (
    <ChartCard icon={BatteryCharging} title="Regen Power vs Battery Limit" testId="chart-regen-power"
      description="DC power the inverter returns to the pack while braking, against the pack's charge-power ceiling (C-rate, temperature, SoC and voltage limits).">
      <ChartZoomHeader caption={firstPassCaption('Power vs Time', zoom, windowS)} isZoomed={zoom.isZoomed} onReset={zoom.resetZoom} testId="zoom-reset-regen-power" />
      <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
        <ComposedChart data={data} margin={{ top: 4, right: 28, left: 0, bottom: 4 }} {...zoomHandlers(zoom)}>
          <CartesianGrid stroke={CHART_GRID} vertical={false} />
          <TimeAxis zoom={zoom} />
          <YAxis yAxisId="p" tick={AXIS_TICK_STYLE} axisLine={{ stroke: CHART_GRID }} tickLine={false} width={52} label={leftLabel('Power (kW)')} />
          <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} labelFormatter={(v) => `t: ${timeTick(v)} s`} formatter={fmt(1)} />
          <Legend wrapperStyle={LEGEND_STYLE} />
          <Line yAxisId="p" type="stepAfter" dataKey="charge_limit_kw" name="Charge limit (kW)" stroke={SERIES_COLORS[6]} strokeDasharray="4 3" dot={false} strokeWidth={1.25} isAnimationActive={false} />
          <Line yAxisId="p" type="stepAfter" dataKey="regen_power_kw" name="Regen power (kW)" stroke={REGEN_COLOR} dot={false} strokeWidth={1.5} isAnimationActive={false} />
          <ZoomArea zoom={zoom} yAxisId="p" />
        </ComposedChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

export function SocTempChart({ series }) {
  const data = useSeriesData(series);
  const zoom = useChartZoom();
  if (!data.length) return null;
  return (
    <ChartCard icon={Thermometer} title="SoC and Battery Temperature" testId="chart-regen-soc-temp"
      description="State of charge and pack temperature (passive thermal model: I²R heat, losses to ambient, no heater or chiller).">
      <ChartZoomHeader caption="SoC & Temp vs Time" isZoomed={zoom.isZoomed} onReset={zoom.resetZoom} testId="zoom-reset-soc-temp" />
      <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
        <ComposedChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 4 }} {...zoomHandlers(zoom)}>
          <CartesianGrid stroke={CHART_GRID} vertical={false} />
          <TimeAxis zoom={zoom} />
          <YAxis yAxisId="soc" tick={AXIS_TICK_STYLE} axisLine={{ stroke: CHART_GRID }} tickLine={false} width={52} label={leftLabel('SoC (%)')} />
          <YAxis yAxisId="t" orientation="right" domain={['auto', 'auto']} tick={AXIS_TICK_STYLE} axisLine={{ stroke: CHART_GRID }} tickLine={false} width={52} label={rightLabel('Temp (°C)')} tickFormatter={(v) => Number(v).toFixed(1)} />
          <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} labelFormatter={(v) => `t: ${timeTick(v)} s`} formatter={fmt(2)} />
          <Legend wrapperStyle={LEGEND_STYLE} />
          <Line yAxisId="soc" type="monotone" dataKey="soc_pct" name="SoC (%)" stroke={SERIES_COLORS[0]} dot={false} strokeWidth={1.5} isAnimationActive={false} />
          <Line yAxisId="t" type="monotone" dataKey="temp_c" name="Battery temp (°C)" stroke={SERIES_COLORS[1]} dot={false} strokeWidth={1.5} isAnimationActive={false} />
          <ZoomArea zoom={zoom} yAxisId="soc" />
        </ComposedChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

const LimiterDot = ({ cx, cy, payload }) => {
  if (payload?.row == null || cx == null || cy == null) return null;
  return <circle cx={cx} cy={cy} r={2.5} fill={LIMITER_COLORS[payload.limiter]} stroke="none" />;
};

export function LimiterTimelineChart({ series, windowS }) {
  const data = useSeriesData(series);
  const zoom = useEventZoom(data, windowS);
  // Rows only for limiters that actually occurred, packed with no gaps.
  const { present, rows } = useMemo(() => {
    const keys = LIMITER_ORDER.filter((k) => data.some((d) => d.limiter === k));
    return { present: keys, rows: data.map((d) => ({ ...d, row: d.limiter ? keys.indexOf(d.limiter) : null })) };
  }, [data]);
  if (!data.length || !present.length) return null;
  const height = Math.max(180, 60 + present.length * 28);
  return (
    <ChartCard icon={ListOrdered} title="Limiter Timeline" testId="chart-regen-limiter"
      description="Which limit set the regen force at each braking step. 'Full regen' means the motor took the whole braking demand.">
      <ChartZoomHeader caption={firstPassCaption('Binding Limit vs Time', zoom, windowS)} isZoomed={zoom.isZoomed} onReset={zoom.resetZoom} testId="zoom-reset-limiter" />
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={rows} margin={{ top: 4, right: 28, left: 0, bottom: 4 }} {...zoomHandlers(zoom)}>
          <CartesianGrid stroke={CHART_GRID} vertical={false} />
          <TimeAxis zoom={zoom} />
          <YAxis
            yAxisId="l" type="number" domain={[-0.5, present.length - 0.5]} ticks={present.map((_, i) => i)}
            interval={0} tickFormatter={(i) => LIMITER_SHORT_LABELS[present[i]] ?? ''}
            tick={AXIS_TICK_STYLE} axisLine={{ stroke: CHART_GRID }} tickLine={false} width={82}
          />
          <Tooltip
            contentStyle={TOOLTIP_CONTENT_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} labelFormatter={(v) => `t: ${timeTick(v)} s`}
            formatter={(v) => (v == null ? '—' : LIMITER_SHORT_LABELS[present[v]])}
          />
          <Line yAxisId="l" dataKey="row" name="Limiter" stroke="none" dot={<LimiterDot />} activeDot={false} isAnimationActive={false} connectNulls={false} />
          <ZoomArea zoom={zoom} yAxisId="l" />
        </ComposedChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

export function CumulativeEnergyChart({ series }) {
  const data = useSeriesData(series);
  const zoom = useChartZoom();
  if (!data.length) return null;
  return (
    <ChartCard icon={Sigma} title="Cumulative Braking Energy" testId="chart-regen-cumulative"
      description="Braking energy at the wheels taken by regen versus lost as heat in the friction brakes.">
      <ChartZoomHeader caption="Energy vs Time" isZoomed={zoom.isZoomed} onReset={zoom.resetZoom} testId="zoom-reset-cumulative" />
      <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
        <ComposedChart data={data} margin={{ top: 4, right: 28, left: 0, bottom: 4 }} {...zoomHandlers(zoom)}>
          <CartesianGrid stroke={CHART_GRID} vertical={false} />
          <TimeAxis zoom={zoom} />
          <YAxis yAxisId="e" tick={AXIS_TICK_STYLE} axisLine={{ stroke: CHART_GRID }} tickLine={false} width={52} label={leftLabel('Energy (kWh)')} tickFormatter={(v) => Number(v).toFixed(2)} />
          <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} labelFormatter={(v) => `t: ${timeTick(v)} s`} formatter={fmt(3)} />
          <Legend wrapperStyle={LEGEND_STYLE} />
          <Line yAxisId="e" type="monotone" dataKey="cum_regen_kwh" name="Regen (kWh)" stroke={REGEN_COLOR} dot={false} strokeWidth={1.75} isAnimationActive={false} />
          <Line yAxisId="e" type="monotone" dataKey="cum_friction_kwh" name="Friction (kWh)" stroke={FRICTION_COLOR} dot={false} strokeWidth={1.75} isAnimationActive={false} />
          <ZoomArea zoom={zoom} yAxisId="e" />
        </ComposedChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

/**
 * Ideal braking force distribution (Ehsani/Gao/Emadi): front vs rear axle braking force at
 * equal adhesion utilisation on both axles, for 0 <= z <= ~peak braking rate. Operating
 * points below the curve are front-biased (stable); above it rear-biased (rear locks first).
 * A scatter — not zoomable, per the site's chart-zoom convention.
 */
export function IdealDistributionChart({ idealCurve, operatingPoints }) {
  const groups = useMemo(() => {
    const stab = [];
    const other = [];
    for (const p of operatingPoints || []) (p.limiter === 'stability' ? stab : other).push(p);
    return { stab, other };
  }, [operatingPoints]);
  if (!idealCurve?.length) return null;
  return (
    <ChartCard icon={Scale} title="Braking Distribution" testId="chart-regen-ideal-curve"
      description="Ideal front/rear braking-force curve with the simulated operating points. Points above the curve brake the rear axle harder than ideal.">
      <p className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Rear vs Front Axle Force</p>
      <ResponsiveContainer width="100%" height={320}>
        <ScatterChart margin={{ top: 4, right: 24, left: 0, bottom: 16 }}>
          <CartesianGrid stroke={CHART_GRID} />
          <XAxis type="number" dataKey="front_kn" name="Front (kN)" tick={AXIS_TICK_STYLE} axisLine={{ stroke: CHART_GRID }} tickLine={false}
            tickFormatter={(v) => Number(v).toFixed(1)}
            label={{ value: 'Front axle force (kN)', position: 'insideBottom', offset: -8, style: AXIS_LABEL_STYLE }} />
          <YAxis type="number" dataKey="rear_kn" name="Rear (kN)" tick={AXIS_TICK_STYLE} axisLine={{ stroke: CHART_GRID }} tickLine={false} width={52}
            tickFormatter={(v) => Number(v).toFixed(1)} label={leftLabel('Rear force (kN)')} />
          <ZAxis range={[14, 14]} />
          <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} formatter={(v) => Number(v).toFixed(2)} />
          <Legend wrapperStyle={LEGEND_STYLE} verticalAlign="top" height={28} />
          <Scatter name="Ideal curve" data={idealCurve} line={{ stroke: CHART_AXIS, strokeWidth: 2 }} shape={() => null} legendType="line" fill={CHART_AXIS} isAnimationActive={false} />
          <Scatter name="Operating points" data={groups.other} fill={SERIES_COLORS[0]} fillOpacity={0.6} isAnimationActive={false} />
          {groups.stab.length > 0 && (
            <Scatter name="Stability-limited" data={groups.stab} fill={FRICTION_COLOR} fillOpacity={0.8} isAnimationActive={false} />
          )}
        </ScatterChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
